import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { completePlay } from "../gamesApi";
import {
  applyTap,
  backgroundOf,
  classifyTap,
  createTapState,
  escapedCount,
  imageInSlot,
  keyToSlot,
  normalizeRound,
  proofOf,
  rulesOf,
  scoreOf,
  slotList,
  targetImagesOf,
} from "./whackAMoleEngine";

const COUNTDOWN_SECONDS = 3;
const HIT_FEEDBACK_MS = 320;
const MISS_FEEDBACK_MS = 260;
const TOUCH_WEAPON_MS = 450;

/**
 * Timed Whack-a-Mole round. Start opens a signed play session (onRoundStart);
 * the session's start response carries the round schedule (playData.round),
 * so where and when targets appear is decided by the server. At the end the
 * hits are submitted and the server computes the score and completion; only
 * a completed round (score ≥ required) is reported to the page for sharing.
 * Each target shows the image the server assigned to its spawn (one of the
 * level's target images); hits are tracked per spawn, never per image.
 */
function WhackAMole({ config, assets, gameTitle, levelName, backTo, playToken, playError, playData, onRoundStart, onComplete, completionSlot }) {
  const rules = rulesOf(config);
  const targets = targetImagesOf(assets);
  const target = targets.length ? targets[0] : null;
  const weapon = assets && assets.weapon && assets.weapon.url ? assets.weapon : null;
  const background = backgroundOf(assets);
  const [phase, setPhase] = useState("intro");
  const [count, setCount] = useState(COUNTDOWN_SECONDS);
  const [now, setNow] = useState(0);
  const [tally, setTally] = useState({ hits: 0, misses: 0 });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [announce, setAnnounce] = useState("");
  const [feedback, setFeedback] = useState(null);
  const roundRef = useRef(null);
  const tapRef = useRef(null);
  const startRef = useRef(0);
  const proofRef = useRef(null);
  const busyRef = useRef(false);
  const boardRef = useRef(null);
  const weaponRef = useRef(null);
  const hideTimer = useRef(0);
  const warnedRef = useRef(false);
  const focusNext = useRef(null);
  const resultRef = useRef(null);
  const boardFocusRef = useRef(null);
  const submitRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = focusNext.current === "result" ? resultRef.current : focusNext.current === "board" ? boardFocusRef.current : null;
    focusNext.current = null;
    if (el) el.focus();
  });

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  const targetCount = targets.length;
  const targetUrls = targets.map((t) => t.url).join("\n");
  useEffect(() => {
    if (!targetUrls || typeof Image === "undefined") return;
    targetUrls.split("\n").forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, [targetUrls]);

  useEffect(() => {
    if (phase !== "starting" || !playToken) return;
    const round = normalizeRound(playData && playData.round, targetCount);
    if (!round) {
      setError("This round couldn’t be set up. Please try again.");
      setPhase("start_error");
      return;
    }
    roundRef.current = round;
    tapRef.current = createTapState(round);
    warnedRef.current = false;
    setTally({ hits: 0, misses: 0 });
    setFeedback(null);
    setNow(0);
    setCount(COUNTDOWN_SECONDS);
    setPhase("countdown");
    if (sectionRef.current && sectionRef.current.scrollIntoView) sectionRef.current.scrollIntoView({ block: "nearest" });
  }, [phase, playToken, playData, targetCount]);

  useEffect(() => {
    if (phase !== "countdown") return undefined;
    if (count <= 0) {
      startRef.current = performance.now();
      setPhase("playing");
      setAnnounce("Go! The round has started.");
      focusNext.current = "board";
      return undefined;
    }
    setAnnounce("Starting in " + count);
    const id = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, count]);

  useEffect(() => {
    if (phase !== "playing") return undefined;
    const round = roundRef.current;
    let frame = 0;
    const tick = () => {
      const t = performance.now() - startRef.current;
      if (t >= round.durationMs) {
        setNow(round.durationMs);
        submitRef.current();
        return;
      }
      if (!warnedRef.current && round.durationMs - t <= 10000 && round.durationMs > 15000) {
        warnedRef.current = true;
        setAnnounce("10 seconds left.");
      }
      setNow(t);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  submitRef.current = () => {
    if (busyRef.current || !tapRef.current) return;
    busyRef.current = true;
    if (!proofRef.current) proofRef.current = proofOf(tapRef.current);
    hideWeapon();
    setError("");
    setPhase("submitting");
    setAnnounce("Round over. Checking your score…");
    completePlay(playToken, proofRef.current).then((res) => {
      busyRef.current = false;
      if (res.ok && res.data && res.data.result) {
        const outcome = res.data.result;
        setResult(outcome);
        setPhase("result");
        focusNext.current = "result";
        setAnnounce("");
        if (outcome.completed && onComplete) onComplete({ completion: res.data.completion || null });
      } else {
        setError(res.error);
        setPhase("submit_error");
      }
    });
  };

  const start = () => {
    if (!rules || !target || !weapon) return;
    busyRef.current = false;
    proofRef.current = null;
    tapRef.current = null;
    roundRef.current = null;
    setTally({ hits: 0, misses: 0 });
    setFeedback(null);
    setNow(0);
    setResult(null);
    setError("");
    setPhase("starting");
    if (onRoundStart) onRoundStart();
  };

  const placeWeapon = (x, y, visible) => {
    const el = weaponRef.current;
    if (!el) return;
    el.style.left = x + "px";
    el.style.top = y + "px";
    el.classList.toggle("is-visible", visible);
  };

  function hideWeapon() {
    clearTimeout(hideTimer.current);
    if (weaponRef.current) weaponRef.current.classList.remove("is-visible", "is-swinging");
  }

  const swing = (x, y, touch) => {
    const el = weaponRef.current;
    if (!el) return;
    placeWeapon(x, y, true);
    el.classList.remove("is-swinging");
    void el.offsetWidth;
    el.classList.add("is-swinging");
    clearTimeout(hideTimer.current);
    if (touch) hideTimer.current = setTimeout(hideWeapon, TOUCH_WEAPON_MS);
  };

  const pointOf = (e) => {
    const rect = boardRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const strike = (slot) => {
    if (phase !== "playing" || !tapRef.current) return;
    const state = tapRef.current;
    const outcome = classifyTap(state, slot, performance.now() - startRef.current, rules);
    if (outcome.kind === "ignored" || outcome.kind === "early") return;
    applyTap(state, outcome);
    setTally({ hits: state.hits.length, misses: state.misses });
    setFeedback({ slot, kind: outcome.kind, at: outcome.t, n: state.hits.length + state.misses });
    if (outcome.kind === "hit") {
      const escaped = escapedCount(state.spawns, outcome.t, state.hitIndexes);
      setAnnounce("Hit! Score " + scoreOf(config, state.hits.length, state.misses, escaped) + ".");
    }
  };

  const onPointerDown = (e) => {
    if (phase !== "playing" || !boardRef.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    const p = pointOf(e);
    swing(p.x, p.y, e.pointerType === "touch");
    const hole = e.target && e.target.closest ? e.target.closest("[data-slot]") : null;
    strike(hole ? Number(hole.getAttribute("data-slot")) : null);
  };

  const onPointerMove = (e) => {
    if (phase !== "playing" || e.pointerType === "touch" || !boardRef.current) return;
    const p = pointOf(e);
    placeWeapon(p.x, p.y, true);
  };

  const onPointerLeave = (e) => {
    if (e.pointerType !== "touch") hideWeapon();
  };

  const strikeHoleByKey = (slot) => {
    const board = boardRef.current;
    const hole = board ? board.querySelector('[data-slot="' + slot + '"]') : null;
    if (hole) {
      const b = board.getBoundingClientRect();
      const r = hole.getBoundingClientRect();
      swing(r.left - b.left + r.width / 2, r.top - b.top + r.height / 2, true);
    }
    strike(slot);
  };

  const onKeyDown = (e) => {
    if (phase !== "playing" || e.repeat) return;
    const slots = rules.rows * rules.cols;
    let slot = keyToSlot(e.key, slots);
    if (slot === null && (e.key === "Enter" || e.key === " ")) {
      const focused = e.target && e.target.getAttribute ? e.target.getAttribute("data-slot") : null;
      slot = focused !== null ? Number(focused) : null;
    }
    if (slot === null) return;
    e.preventDefault();
    strikeHoleByKey(slot);
  };

  const back = backTo ? (
    <Link to={backTo.path} className="btn btn-outline-secondary wam__back">
      {backTo.label}
    </Link>
  ) : null;

  if (!rules || !target || !weapon) {
    return (
      <div className="games-panel games-panel--empty" role="alert">
        <p className="games-panel__body">This level isn’t ready to play yet.</p>
      </div>
    );
  }

  const live = (
    <p className="wam__sr-only" role="status" aria-live="polite" data-testid="wam-live">
      {announce}
    </p>
  );

  if (playError && (phase === "starting" || phase === "countdown")) {
    return (
      <div className="games-panel games-panel--error wam" role="alert">
        <p className="games-panel__body">{playError}</p>
        <button type="button" className="btn btn-danger" onClick={start}>
          Try again
        </button>{" "}
        {back}
      </div>
    );
  }

  if (phase === "start_error") {
    return (
      <div className="games-panel games-panel--error wam" role="alert">
        <p className="games-panel__body">{error}</p>
        <button type="button" className="btn btn-danger" onClick={start}>
          Try again
        </button>
      </div>
    );
  }

  if (phase === "intro") {
    const penalties = [];
    if (rules.missPenalty) penalties.push("each miss costs " + rules.missPenalty);
    if (rules.escapePenalty) penalties.push("each target that gets away costs " + rules.escapePenalty);
    const slotCount = rules.rows * rules.cols;
    return (
      <section className="wam wam--intro" aria-labelledby="wam-intro-title" data-testid="wam-intro">
        <h3 id="wam-intro-title" className="wam__title">
          {levelName}
        </h3>
        <div className="wam__intro-art" aria-hidden="true">
          <img className="wam__intro-target" src={target.url} alt="" draggable="false" />
          <img className="wam__intro-weapon" src={weapon.url} alt="" draggable="false" />
        </div>
        <p className="wam__rules">
          Hit the targets as they pop up. You have <strong>{rules.durationSeconds} seconds</strong>; each hit scores{" "}
          <strong>{rules.pointsPerHit}</strong>
          {penalties.length ? ", " + penalties.join(", ") : ""}.
        </p>
        <p className="wam__rules" data-testid="wam-goal">
          {rules.requiredScore > 0 ? (
            <React.Fragment>
              Score <strong>{rules.requiredScore}</strong> or more to complete the level ({rules.targets} targets, up to {rules.maxScore} points).
            </React.Fragment>
          ) : (
            <React.Fragment>Play the round to the end to complete the level.</React.Fragment>
          )}
        </p>
        <p className="wam__hint">
          Keyboard: press {slotCount > 9 ? "1–9 for the first nine holes" : "1–" + slotCount + " for the holes in reading order"}, or Tab to a hole and press Enter.
        </p>
        <button type="button" className="btn btn-danger wam__start" onClick={start} data-testid="wam-start">
          Start
        </button>
      </section>
    );
  }

  if (phase === "result" && result) {
    const required = result.required_score;
    let status;
    if (result.completed) status = required > 0 ? "You reached the target score of " + required + "." : "You finished the round.";
    else status = "You finished the round but scored less than the " + required + " points needed.";
    return (
      <section className="wam wam--result" aria-labelledby="wam-result-title" data-testid="wam-result">
        <h3 id="wam-result-title" className="wam__result-title" tabIndex={-1} ref={resultRef}>
          {result.completed ? "Level complete!" : "Round over"}
        </h3>
        <p className="wam__result-level">
          {gameTitle} · {levelName}
        </p>
        <p className={"wam__result-status" + (result.completed ? " is-complete" : " is-short")} data-testid="wam-result-status">
          {status}
        </p>
        <dl className="wam__stats">
          <div className="wam__stat wam__stat--score">
            <dt>Score</dt>
            <dd data-testid="wam-result-score">{result.score}</dd>
          </div>
          <div className="wam__stat">
            <dt>Hits</dt>
            <dd>
              {result.hits} of {result.targets}
            </dd>
          </div>
          <div className="wam__stat">
            <dt>Misses</dt>
            <dd>{result.misses}</dd>
          </div>
          <div className="wam__stat">
            <dt>Got away</dt>
            <dd>{result.escaped}</dd>
          </div>
          <div className="wam__stat">
            <dt>Round finished</dt>
            <dd>{result.finished ? "Yes" : "No"}</dd>
          </div>
          <div className="wam__stat">
            <dt>Target score {required > 0 ? "(" + required + ")" : ""}</dt>
            <dd>{required > 0 ? (result.target_reached ? "Reached" : "Not reached") : "None"}</dd>
          </div>
        </dl>
        <div className="wam__actions">
          <button type="button" className="btn btn-danger" onClick={start} data-testid="wam-replay">
            Replay
          </button>
          {back}
        </div>
        {result.completed ? completionSlot : null}
      </section>
    );
  }

  if (phase === "submitting" || phase === "submit_error") {
    return (
      <div className="games-panel wam" role={phase === "submit_error" ? "alert" : "status"} aria-live="polite">
        {phase === "submitting" ? (
          <p className="games-panel__body">Round over — checking your score…</p>
        ) : (
          <React.Fragment>
            <p className="games-panel__body">{error}</p>
            <button type="button" className="btn btn-danger" onClick={() => submitRef.current()}>
              Try again
            </button>{" "}
            <button type="button" className="btn btn-outline-secondary" onClick={start}>
              Play again
            </button>
          </React.Fragment>
        )}
      </div>
    );
  }

  const round = roundRef.current;
  const state = tapRef.current;
  const playing = phase === "playing" && round && state;
  const t = playing ? now : 0;
  const escaped = playing ? escapedCount(round.spawns, t, state.hitIndexes) : 0;
  const score = scoreOf(config, tally.hits, tally.misses, escaped);
  const timeLeft = round ? Math.max(0, Math.ceil((round.durationMs - t) / 1000)) : rules.durationSeconds;
  const hitAt = new Map();
  if (state) state.hits.forEach((h) => hitAt.set(h.index, h.t));
  const shown = new Map();
  if (playing) {
    round.spawns.forEach((s) => {
      if (s.at <= t && t < s.end) {
        const hitTime = hitAt.get(s.index);
        if (hitTime === undefined) shown.set(s.slot, "up");
        else if (t - hitTime < HIT_FEEDBACK_MS) shown.set(s.slot, "hit");
      }
    });
  }

  return (
    <section className="wam wam--play" aria-label={"Whack-a-Mole: " + levelName} data-testid="wam-play" ref={sectionRef}>
      <div className="wam__hud" data-testid="wam-hud">
        <span className="wam__hud-item">
          Score <strong data-testid="wam-score">{score}</strong>
        </span>
        <span className="wam__hud-item">
          Time <strong data-testid="wam-time">{timeLeft}s</strong>
        </span>
        <span className="wam__hud-item">
          Hits <strong data-testid="wam-hits">{tally.hits}</strong>
        </span>
        <span className="wam__hud-item">
          Misses <strong data-testid="wam-misses">{tally.misses}</strong>
        </span>
        <span className="wam__hud-item">
          Goal <strong>{rules.requiredScore > 0 ? rules.requiredScore : "finish"}</strong>
        </span>
      </div>
      <div
        className={"wam__board" + (playing ? " is-playing" : "") + (background ? " has-backdrop" : "")}
        ref={boardRef}
        style={{ "--wam-cols": rules.cols, "--wam-rows": rules.rows, "--wam-scale": rules.targetScale / 100 }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        onKeyDown={onKeyDown}
        onContextMenu={(e) => e.preventDefault()}
        role="group"
        aria-label="Board"
        data-testid="wam-board"
      >
        {background ? <img className="wam__backdrop" src={background.url} alt="" draggable="false" data-testid="wam-backdrop" /> : null}
        {slotList(rules.rows, rules.cols).map((slot) => {
          const look = shown.get(slot) || "";
          const flash = feedback && feedback.slot === slot && feedback.kind === "miss" && t - feedback.at < MISS_FEEDBACK_MS;
          const holeImage = (round && targets[imageInSlot(round.spawns, slot, t)]) || target;
          return (
            <button
              type="button"
              key={slot}
              className={"wam__hole" + (look ? " is-" + look : "") + (flash ? " is-miss" : "")}
              data-slot={slot}
              tabIndex={playing ? 0 : -1}
              ref={slot === 0 ? boardFocusRef : undefined}
              aria-label={"Hole " + (slot + 1) + (look === "up" ? ", target showing" : "")}
            >
              <span className="wam__pit" aria-hidden="true" />
              <span className="wam__window" aria-hidden="true">
                <img className="wam__target" src={holeImage.url} alt="" draggable="false" />
              </span>
            </button>
          );
        })}
        <div className="wam__weapon" ref={weaponRef} aria-hidden="true">
          <img src={weapon.url} alt="" draggable="false" />
        </div>
        {phase === "countdown" || phase === "starting" ? (
          <div className="wam__overlay" aria-hidden="true">
            <span className="wam__count">{phase === "countdown" ? count : "…"}</span>
          </div>
        ) : null}
      </div>
      {live}
    </section>
  );
}

export default WhackAMole;
